export const DRIVER_OFFLINE_ORDER_PACK_HOURS = 48;
export const DRIVER_OFFLINE_ORDER_PACK_UPDATED_EVENT = "driver-offline-order-pack-updated";

const DATABASE_NAME = "big_taxi_driver_offline_order_pack_v1";
const DATABASE_VERSION = 1;
const SIGNBOARD_CACHE_PREFIX = "company-signboard-no-qr-v2:";
const STORE_NAME = "offline_order_packs";
const TERMINAL_ORDER_STATUSES = new Set(["cancelled", "canceled", "refunded", "deleted", "completed"]);

function getDriverId(driverId) {
  return String(driverId || "").trim();
}

function getOrderId(order) {
  return String(order?.id || order?.order_id || "").trim();
}

function getOrderPackKey(driverId, orderId) {
  return `${driverId}:${orderId}`;
}

function getDepartureAt(order) {
  const date = String(order?.departure_date || order?.service_date || "").trim();
  const time = String(order?.pickup_time || order?.departure_time || order?.service_time || "00:00").trim();
  const dateMatch = date.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const timeMatch = time.match(/^(\d{1,2}):(\d{2})/);
  if (!dateMatch) return 0;

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]) - 1;
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch?.[1] || 0);
  const minute = Number(timeMatch?.[2] || 0);
  return Date.UTC(year, month, day, hour - 9, minute, 0);
}

export function isFutureDriverOrderPackEligible(order, now = Date.now()) {
  const orderStatus = String(order?.status || "").toLowerCase();
  const driverStatus = String(order?.driver_order_status || "").toLowerCase();
  if (TERMINAL_ORDER_STATUSES.has(orderStatus) || TERMINAL_ORDER_STATUSES.has(driverStatus)) return false;

  const departureAt = getDepartureAt(order);
  if (!departureAt) return false;
  return departureAt >= now - (2 * 60 * 60 * 1000)
    && departureAt <= now + (DRIVER_OFFLINE_ORDER_PACK_HOURS * 60 * 60 * 1000);
}

export function getFutureDriverOrderPackCandidates(orders, now = Date.now()) {
  const seen = new Set();
  return (Array.isArray(orders) ? orders : [])
    .filter((order) => {
      const orderId = getOrderId(order);
      if (!orderId || seen.has(orderId) || !isFutureDriverOrderPackEligible(order, now)) return false;
      seen.add(orderId);
      return true;
    })
    .sort((left, right) => getDepartureAt(left) - getDepartureAt(right));
}

function getOrderSourceSignature(order) {
  return SIGNBOARD_CACHE_PREFIX + JSON.stringify(order || {});
}

function hasIndexedDb() {
  return typeof window !== "undefined" && typeof window.indexedDB !== "undefined";
}

function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

function requestAsPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("OFFLINE_PACK_DB_REQUEST_FAILED"));
  });
}

function transactionAsPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error("OFFLINE_PACK_DB_TRANSACTION_FAILED"));
    transaction.onabort = () => reject(transaction.error || new Error("OFFLINE_PACK_DB_TRANSACTION_ABORTED"));
  });
}

async function openDatabase() {
  if (!hasIndexedDb()) return null;
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("OFFLINE_PACK_DB_OPEN_FAILED"));
  });
}

async function readAllPacks() {
  const database = await openDatabase();
  if (!database) return [];
  try {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const complete = transactionAsPromise(transaction);
    const records = await requestAsPromise(transaction.objectStore(STORE_NAME).getAll());
    await complete;
    return Array.isArray(records) ? records : [];
  } finally {
    database.close();
  }
}

async function writePack(record) {
  const database = await openDatabase();
  if (!database) return false;
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const complete = transactionAsPromise(transaction);
    transaction.objectStore(STORE_NAME).put(record);
    await complete;
    return true;
  } finally {
    database.close();
  }
}

async function deletePackKeys(keys) {
  if (!keys.length) return;
  const database = await openDatabase();
  if (!database) return;
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const complete = transactionAsPromise(transaction);
    const store = transaction.objectStore(STORE_NAME);
    keys.forEach((key) => store.delete(key));
    await complete;
  } finally {
    database.close();
  }
}

function isRecordExpired(record, now = Date.now()) {
  const expiresAt = Date.parse(record?.expires_at || "");
  return !Number.isFinite(expiresAt) || expiresAt <= now;
}

function notifyOfflineOrderPackUpdated(summary) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(DRIVER_OFFLINE_ORDER_PACK_UPDATED_EVENT, { detail: summary }));
  } catch {
    // The cache remains usable in older WebViews even when events are unavailable.
  }
}

async function getUsableDriverPacks(driverId, { purgeExpired = false, purgeForeign = false } = {}) {
  const normalizedDriverId = getDriverId(driverId);
  if (!normalizedDriverId) return [];
  const now = Date.now();
  const records = await readAllPacks();
  const keysToDelete = records
    .filter((record) => isRecordExpired(record, now) || (purgeForeign && record.driver_id !== normalizedDriverId))
    .map((record) => record.key)
    .filter(Boolean);
  if (purgeExpired || purgeForeign) {
    await deletePackKeys(keysToDelete);
  }
  return records.filter((record) => (
    record.driver_id === normalizedDriverId
    && !isRecordExpired(record, now)
  ));
}

export async function getDriverOfflineOrderPackSummary(driverId) {
  try {
    const packs = await getUsableDriverPacks(driverId, { purgeExpired: true });
    const pdfCount = packs.filter((pack) => isBlob(pack.signboard_pdf) && pack.source_signature?.startsWith(SIGNBOARD_CACHE_PREFIX)).length;
    return {
      orderCount: packs.length,
      pdfCount,
      pendingPdfCount: Math.max(0, packs.length - pdfCount),
      updatedAt: packs.reduce((latest, pack) => latest > (pack.cached_at || "") ? latest : (pack.cached_at || ""), ""),
    };
  } catch {
    return { orderCount: 0, pdfCount: 0, pendingPdfCount: 0, updatedAt: "" };
  }
}

export async function loadCachedDriverOfflineOrderPack(driverId, orderId) {
  const normalizedDriverId = getDriverId(driverId);
  const normalizedOrderId = String(orderId || "").trim();
  if (!normalizedDriverId || !normalizedOrderId) return null;
  try {
    const packs = await getUsableDriverPacks(normalizedDriverId, { purgeExpired: true });
    return packs.find((pack) => pack.order_id === normalizedOrderId) || null;
  } catch {
    return null;
  }
}

export async function loadCachedDriverOfflineSignboardPdf(driverId, orderId) {
  const pack = await loadCachedDriverOfflineOrderPack(driverId, orderId);
  return isBlob(pack?.signboard_pdf) && pack.source_signature?.startsWith(SIGNBOARD_CACHE_PREFIX) ? pack.signboard_pdf : null;
}

export function openCachedDriverOfflineSignboardPdf(pdfBlob) {
  if (!isBlob(pdfBlob) || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new Error("OFFLINE_SIGNBOARD_UNAVAILABLE");
  }
  const blobUrl = URL.createObjectURL(pdfBlob);
  window.location.assign(blobUrl);
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
  return blobUrl;
}

export async function cacheFutureDriverOrderPack({ driverId, orders, fetchOrderDetail, fetchSignboardPdf, now = Date.now() }) {
  const normalizedDriverId = getDriverId(driverId);
  if (!normalizedDriverId || typeof fetchOrderDetail !== "function") {
    return getDriverOfflineOrderPackSummary(normalizedDriverId);
  }

  try {
    const existingPacks = await getUsableDriverPacks(normalizedDriverId, { purgeExpired: true, purgeForeign: true });
    const candidates = getFutureDriverOrderPackCandidates(orders, now);
    const candidateOrderIds = new Set(candidates.map((order) => getOrderId(order)));
    await deletePackKeys(existingPacks
      .filter((pack) => !candidateOrderIds.has(pack.order_id))
      .map((pack) => pack.key)
      .filter(Boolean));
    const existingByOrderId = new Map(existingPacks
      .filter((pack) => candidateOrderIds.has(pack.order_id))
      .map((pack) => [pack.order_id, pack]));

    for (const summaryOrder of candidates) {
      const orderId = getOrderId(summaryOrder);
      const existing = existingByOrderId.get(orderId);
      const sourceSignature = getOrderSourceSignature(summaryOrder);
      const refreshOrder = !existing?.order || existing.source_signature !== sourceSignature;
      let order = existing?.order || null;
      let signboardPdf = existing?.source_signature?.startsWith(SIGNBOARD_CACHE_PREFIX) ? existing.signboard_pdf || null : null;

      try {
        if (refreshOrder) {
          order = await fetchOrderDetail(orderId);
        }
        if (!order) continue;

        if (typeof fetchSignboardPdf === "function" && (!signboardPdf || refreshOrder)) {
          const pdfResponse = await fetchSignboardPdf(orderId);
          const nextPdf = pdfResponse?.data ?? pdfResponse;
          if (isBlob(nextPdf)) {
            signboardPdf = nextPdf;
          }
        }

        const departureAt = getDepartureAt(order) || getDepartureAt(summaryOrder);
        const expiresAt = departureAt > now
          ? departureAt + (24 * 60 * 60 * 1000)
          : now + (2 * 60 * 60 * 1000);
        await writePack({
          key: getOrderPackKey(normalizedDriverId, orderId),
          driver_id: normalizedDriverId,
          order_id: orderId,
          departure_at: departureAt,
          order,
          source_signature: sourceSignature,
          signboard_pdf: signboardPdf,
          cached_at: new Date().toISOString(),
          expires_at: new Date(expiresAt).toISOString(),
        });
      } catch {
        // Preserve any previous pack. A single PDF or detail request must not clear offline access.
      }
    }

    const summary = await getDriverOfflineOrderPackSummary(normalizedDriverId);
    notifyOfflineOrderPackUpdated(summary);
    return summary;
  } catch {
    return getDriverOfflineOrderPackSummary(normalizedDriverId);
  }
}

export async function clearDriverOfflineOrderPacksExcept(driverId) {
  const normalizedDriverId = getDriverId(driverId);
  if (!normalizedDriverId) return;
  try {
    await getUsableDriverPacks(normalizedDriverId, { purgeExpired: true, purgeForeign: true });
    notifyOfflineOrderPackUpdated(await getDriverOfflineOrderPackSummary(normalizedDriverId));
  } catch {
    // Clearing cache is best effort and must never block login.
  }
}

export async function clearDriverOfflineOrderPacks(driverId) {
  const normalizedDriverId = getDriverId(driverId);
  if (!normalizedDriverId) return;
  try {
    const records = await readAllPacks();
    await deletePackKeys(records
      .filter((record) => record.driver_id === normalizedDriverId)
      .map((record) => record.key)
      .filter(Boolean));
    notifyOfflineOrderPackUpdated({ orderCount: 0, pdfCount: 0, pendingPdfCount: 0, updatedAt: "" });
  } catch {
    // Explicit logout must still complete even if IndexedDB is unavailable.
  }
}
